import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import WhatsAppButton from '../components/common/WhatsAppButton';
import AppSidebar from '../components/app/AppSidebar';
import AppHeaderBar from '../components/app/AppHeaderBar';
import AppFooter from '../components/app/AppFooter';
import ProfileGateBanner from '../components/app/ProfileGateBanner';
import { useAuth } from '../contexts/AuthContext';
import { getReportInputStatus, getUserProfile, ReportInputStatus } from '../services/userService';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const isLogin = location.pathname === '/app/login';
  const { currentUser, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reportInput, setReportInput] = useState<ReportInputStatus | null>(null);

  // Close the mobile menu whenever we navigate to a new route.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // App-wide profile gate: the face photo + measurements requirement is shown
  // on EVERY app page until satisfied. Re-checked on each navigation so the
  // banner disappears right after the customer completes their profile.
  useEffect(() => {
    let mounted = true;
    if (!currentUser) {
      setReportInput(null);
      return;
    }
    getUserProfile(currentUser.uid)
      .then((p) => {
        if (mounted) setReportInput(getReportInputStatus(p));
      })
      .catch(() => {
        // Missing profile read must never break the app shell; hide the banner.
        if (mounted) setReportInput(null);
      });
    return () => { mounted = false; };
  }, [currentUser, location.pathname]);

  // Don't nag on the page where they're already fixing it.
  const onCompleteProfilePage = location.pathname === '/app/complete-profile';

  // Auth guard: every /app/* path except /app/login requires login.
  if (!isLogin) {
    if (loading) return null;
    if (!currentUser) {
      const next = `${location.pathname}${location.search || ''}`;
      return <Navigate to={`/app/login?next=${encodeURIComponent(next)}`} replace />;
    }
  }

  // Login uses the public marketing chrome so visitors can see the brand.
  if (isLogin) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow bg-off-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <Outlet />
          </div>
        </main>
        <Footer />
        <WhatsAppButton floating />
      </div>
    );
  }

  // Authenticated app shell: persistent sidebar + main pane.
  return (
    <div className="min-h-screen bg-off-white">
      <AppSidebar open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Main content area, offset for the fixed sidebar on desktop */}
      <div className="lg:pl-72 flex flex-col min-h-screen">
        <AppHeaderBar onOpenMenu={() => setMobileMenuOpen(true)} />
        <main className="flex-grow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
            {reportInput && !reportInput.ok && !onCompleteProfilePage ? (
              <ProfileGateBanner
                status={reportInput}
                next={`${location.pathname}${location.search || ''}`}
              />
            ) : null}
            <Outlet />
          </div>
        </main>
        <AppFooter />
      </div>

      <WhatsAppButton floating />
    </div>
  );
};

export default AppLayout;
